import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
import { UserColor } from './users.js';

/** Espace Retours (CDC §3.2 `feedback`, §3.7) : amélioration continue de l'appli. */

export const FeedbackType = z.enum(['BUG', 'ODD', 'FAILED_ACTION', 'IDEA']);
export type FeedbackType = z.infer<typeof FeedbackType>;

/** Libellés de la maquette (liste « C'est… »). */
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  BUG: 'Un bug',
  ODD: "Quelque chose d'anormal",
  FAILED_ACTION: "Une action qui n'a pas fonctionné",
  IDEA: 'Une idée',
};

export const FeedbackStatus = z.enum(['NEW', 'TO_CHECK', 'FIXED', 'NOT_A_BUG']);
export type FeedbackStatus = z.infer<typeof FeedbackStatus>;

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  NEW: 'Nouveau',
  TO_CHECK: 'À vérifier',
  FIXED: 'Corrigé',
  NOT_A_BUG: 'Pas un bug',
};

/** Statuts « à traiter » (filtre par défaut et export). */
export const OPEN_FEEDBACK_STATUSES: readonly FeedbackStatus[] = ['NEW', 'TO_CHECK'];

export function isOpenFeedback(status: FeedbackStatus): boolean {
  return OPEN_FEEDBACK_STATUSES.includes(status);
}

/** Pièces jointes : images seulement, 5 Mo au plus chacune, 5 par retour (lot AQ). */
export const FEEDBACK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const FEEDBACK_ATTACHMENT_MAX_COUNT = 5;
export const FEEDBACK_ATTACHMENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;
export type FeedbackAttachmentType = (typeof FEEDBACK_ATTACHMENT_TYPES)[number];

/** Lot AQ : une capture d'un retour, lue à l'adresse authentifiée `url`. */
export const FeedbackAttachment = z.object({
  id: Uuid,
  /** `GET /api/feedback/:id/attachments/:attachmentId` (membres connectés seulement). */
  url: z.string(),
  mime: z.enum(FEEDBACK_ATTACHMENT_TYPES),
});
export type FeedbackAttachment = z.infer<typeof FeedbackAttachment>;

/** Contexte ajouté automatiquement (écran, navigateur, version, derniers plans validés). */
export const FeedbackContext = z.object({
  /** Chemin de l'écran d'où vient le retour (ex. `/chiffres`). */
  route: z.string().nullable(),
  userAgent: z.string().nullable(),
  appVersion: z.string(),
  /** Les 5 derniers plans exécutés par l'auteur, du plus récent au plus ancien. */
  lastPlans: z.array(
    z.object({ id: Uuid, kind: z.string(), status: z.string(), executedAt: IsoDateTime.nullable() }),
  ),
  /** Lot AD : retour rédigé avec l'assistant : la conversation (lisible par l'administrateur et l'auteur). */
  conversationId: Uuid.nullable().optional(),
  /** Lot AD : ce que l'assistant a constaté dans les données (modifiable avant l'envoi). */
  observed: z.string().nullable().optional(),
});
export type FeedbackContext = z.infer<typeof FeedbackContext>;

export const FeedbackAuthor = z.object({
  id: Uuid,
  name: z.string(),
  initials: z.string(),
  color: UserColor,
});
export type FeedbackAuthor = z.infer<typeof FeedbackAuthor>;

export const Feedback = z.object({
  id: Uuid,
  type: FeedbackType,
  /** Écran concerné, tel qu'affiché (« Chiffres », « À faire »…). */
  screen: z.string(),
  text: z.string(),
  status: FeedbackStatus,
  answer: z.string().nullable(),
  /** Lot AQ : les captures (5 au plus), dans l'ordre d'envoi. */
  attachments: z.array(FeedbackAttachment),
  context: FeedbackContext.nullable(),
  author: FeedbackAuthor.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime.nullable(),
});
export type Feedback = z.infer<typeof Feedback>;

/** `GET /api/feedback?status=open|all` : tableau brut (non paginé), du plus récent au plus ancien. */
export const FeedbackListQuery = z.object({ status: z.enum(['open', 'all']).default('all') });
export const FeedbackList = z.array(Feedback);
export type FeedbackList = z.infer<typeof FeedbackList>;

/** `POST /api/feedback` : le serveur complète le contexte (navigateur, version, plans). */
export const CreateFeedbackBody = z.object({
  type: FeedbackType,
  screen: z.string().trim().min(1, 'Écran obligatoire').max(80),
  text: z.string().trim().min(1, "Décris en une phrase ce qui s'est passé.").max(5000),
  /** Chemin de l'écran d'origine (complète le contexte). */
  route: z.string().max(500).optional(),
  /** Lot AD : conversation de l'assistant d'où vient le retour (celle de l'auteur, sinon 404). */
  conversationId: Uuid.optional(),
  /** Lot AD : constat de l'assistant, modifiable. */
  observed: z.string().trim().max(2000).optional(),
});
export type CreateFeedbackBody = z.input<typeof CreateFeedbackBody>;

/** `PATCH /api/feedback/:id` : administrateur seulement (statut et réponse). */
export const UpdateFeedbackBody = z
  .object({
    status: FeedbackStatus.optional(),
    /** Chaîne vide : retire la réponse. */
    answer: z.string().trim().max(2000).optional(),
  })
  .refine((body) => body.status !== undefined || body.answer !== undefined, {
    message: 'Aucune modification demandée',
  });
export type UpdateFeedbackBody = z.input<typeof UpdateFeedbackBody>;

export const FeedbackIdParams = z.object({ id: Uuid });
export const FeedbackAttachmentParams = z.object({ id: Uuid, attachmentId: Uuid });

/** Lot AD : type de retour d'une proposition de l'assistant (`bug` → BUG, `idea` → IDEA). */
export function feedbackTypeOfProposal(type: 'bug' | 'idea'): FeedbackType {
  return type === 'idea' ? 'IDEA' : 'BUG';
}
